import React, { useState } from 'react';
import { ApiHistoryEntry } from '@/types/ApiDocs.ts';
import { cn } from '@/lib/utils.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Clock, ChevronDown, ChevronLeft, Trash2 } from 'lucide-react';
import { METHOD_COLORS } from '../utils.ts';

interface HistoryPanelProps {
    history: ApiHistoryEntry[];
    onClear: () => void;
}

export function HistoryPanel({ history, onClear }: HistoryPanelProps) {
    const [expanded, setExpanded] = useState<string | null>(null);

    if (history.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                <Clock className="size-8" />
                <p className="text-sm">אין היסטוריה עדיין</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                    <Clock className="size-4" />
                    היסטוריה
                </h3>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClear}
                    className="text-red-500 hover:text-red-700 text-xs"
                >
                    <Trash2 className="size-3 ml-1" /> נקה הכל
                </Button>
            </div>
            {[...history].reverse().map((entry) => (
                <div key={entry.id} className="border rounded-lg overflow-hidden">
                    <button
                        onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                        className="w-full flex items-center gap-3 p-3 text-sm hover:bg-gray-50 transition-colors"
                    >
                        <span
                            className={cn(
                                'px-1.5 py-0.5 rounded text-[11px] font-bold',
                                METHOD_COLORS[entry.method],
                            )}
                        >
                            {entry.method}
                        </span>
                        <code className="text-gray-600 flex-1 text-left truncate" dir="ltr">
                            {entry.url}
                        </code>
                        <span
                            className={cn(
                                'text-xs font-bold',
                                entry.status >= 200 && entry.status < 300
                                    ? 'text-green-600'
                                    : 'text-red-600',
                            )}
                        >
                            {entry.status}
                        </span>
                        <span className="text-gray-400 text-[11px] shrink-0">
                            {new Date(entry.timestamp).toLocaleTimeString('he-IL')}
                        </span>
                        {expanded === entry.id
                            ? <ChevronDown className="size-4 text-gray-400" />
                            : <ChevronLeft className="size-4 text-gray-400" />}
                    </button>
                    {expanded === entry.id && (
                        <div className="border-t bg-gray-50 p-3">
                            <pre className="text-xs overflow-auto max-h-48 text-gray-700" dir="ltr">
                                {JSON.stringify(entry.response, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
